import styled from "@emotion/styled";
import { nanoid } from "nanoid";
import { useMemo } from "react";
import { useTable, usePagination } from "react-table";
import SkelentonLoader from "./Loaders/SkeletonTableLoader";
import NoData from "./NoData";
import Paginator from "./Paginator";

interface BasicTableProps {
  data: any[];
  columns: any;
  loading: boolean;
  selectedRow?: (rowValue: any) => void;
}

const BasicTable = (props: BasicTableProps) => {
  const { data: TData = [], columns: TColumn, loading, selectedRow } = props;
  const columns = useMemo(() => TColumn, [TColumn]);
  const data = useMemo(() => TData, [TData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    pageOptions,
    state,
    prepareRow,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
    },
    usePagination,
  );
  const rowClicked = (rowValue: any) => {
    if (selectedRow != null) {
      selectedRow(rowValue.original);
    }
  };

  const { pageIndex, pageSize } = state;
  return (
    <BasicTable.Wrapper>
      <div className="tableWrap">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={nanoid()} {...headerGroup.getHeaderGroupProps()}>
                <th>S/N</th>
                {headerGroup.headers.map((column) => (
                  <th key={nanoid()} {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {loading ? (
            <tbody>
              <SkelentonLoader colSpan={columns.length} />
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()}>
              {page.map((row, index: number) => {
                prepareRow(row);
                return (
                  <tr
                    key={nanoid()}
                    {...row.getRowProps()}
                    onClick={() => {
                      rowClicked(row);
                    }}
                    className={`${selectedRow != null ? "cursor" : ""}`}
                  >
                    <td>{index + 1}</td>
                    {row.cells.map((cell) => {
                      return (
                        <td key={nanoid()} {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {!loading && data.length === 0 ? <NoData /> : <div />}
      {data.length > 0 ? (
        <Paginator
          pageCount={pageOptions.length}
          onPageChange={({ selected }) => gotoPage(selected)}
          pageNo={pageIndex}
          pageSize={pageSize}
          onSizeChange={(size) => setPageSize(Number(size))}
        />
      ) : null}
    </BasicTable.Wrapper>
  );
};

BasicTable.Wrapper = styled.div`
  .tableWrap {
    overflow-y: auto; /* make the table scrollable if height is more than 200 px  */
    height: 600px; /* gives an initial height of 200px to the table */
  }
  .tableWrap thead th {
    position: sticky; /* make the table heads sticky */
    top: 0px; /* table head will be placed from the top of the table and sticks to it */
    z-index: 10;
  }
  table {
    border-collapse: collapse; /* make the table borders collapse to each other */
    width: 100%;
  }
  th,
  td {
    padding: 8px 16px;
    border: 1px solid #ccc;
  }
  th {
    background: #eee;
  }
`;
// BasicTableT.Wrapper = styled.div`
//   /* This is required to make the table full-width */
//   display: block;
//   max-width: 100%;

//   /* This will make the table scrollable when it gets too small */
//   .tableWrap {
//     display: block;
//     max-width: 100%;
//     overflow-x: scroll;
//     overflow-y: hidden;
//     border-bottom: 1px solid black;
//   }

//   table {
//     /* Make sure the inner table is always as wide as needed */
//     width: 100%;
//     border-spacing: 0;

//     tr {
//       :last-child {
//         td {
//           border-bottom: 0;
//         }
//       }
//     }

//     th,
//     td {
//       margin: 0;
//       /* The secret sauce */
//       /* Each cell should grow equally */
//       width: 1%;
//       /* But "collapsed" cells should be as small as possible */
//       &.collapse {
//         width: 0.0000000001%;
//       }

//       :last-child {
//         border-right: 0;
//       }
//     }
//     th {
//       background-color: #edf2f7;
//       color: #1d294c;
//       padding: 0.8rem;
//       text-transform: uppercase;
//     }
//     td {
//       color: #1d294c;
//       border-top: 1px solid #ddd;
//       border-bottom: 1px solid #ddd;
//       padding: 15px;
//       text-align: center;
//     }
//   }
// `;

export default BasicTable;
